import * as React from 'react';
import { AddressDTO, CustomerForEditionDTO, Permission, Res, SalesOrderType, StartTakeOutOrderParamsDTO } from 'core/api';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { ActionButtonDefinition, Keypad, PosScreenClassComponent, ScreenSize, ThemeManager } from 'pos/ui';
import * as TakeOut from './StartTakeOutData';
import createDeliveryAddress from './create-delivery-address';
import { CustomerFacade } from 'pos/facades';
import * as SalesStore from '../SalesStore';
import DeliveryAddresses from 'pos/modules/helpers/deliveryAddress/DeliveryAddresses';
import { isBillingAddress } from 'pos/modules/helpers/deliveryAddress';
import { Flex } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { legacyControlsFor } from 'core/ui-next/controls';

type Props = Modal.Actions<StartTakeOutOrderParamsDTO>
type State = TakeOut.Data & { searchPhone: string };

class StartTakeOutOrdersScreen extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();
	private txtPhone = React.createRef<UI.TextBox<State>>();

	protected getScreenId(): string {
		return 'START_TAKE_OUT_ORDER';
	}

	protected getTitle(): string {
		return Res.Web.START_TAKEOUT_ORDER();
	}

	protected getImage(): string | SvgIcon {
		return Images.StartTakeOutOrder;
	}

	protected getSize(): ScreenSize {
		return { width: 1000, height: 700 };
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			this.okButton(this.accept)
		];
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	protected useKeypad() {
		return false;
	}

	private accept = async () => {
		const valid = await this.form.current?.updateModel();
		if (!valid)
			return;

		const result = TakeOut.validate(this.form.current!.model);
		if (result.ok) {

			const showNotes = this.state.customer.ShowNotes && this.state.customer.Notes;
			if (showNotes) {
				await Message.show(this.state.customer.Notes);
			}

			this.close(TakeOut.getParams(this.form.current!.model));
			return;
		}

		await Message.show(result.error);
	};

	createInitialState(_props: Props): Nullable<State> {
		return {
			searchPhone: '',
			...TakeOut.emptyData()
		};
	}

	private searchByPhone = (phone: string) => {
		this.setState({ searchPhone: phone }, async () => {
			if (phone.length < 3) {
				this.clearState();
			}
			else {
				const customer = await CustomerFacade.getByPhone(phone);
				this.showCustomer(customer);
			}
		});
	};

	private searchCustomer = async () => {
		const result = await Command.execute('searchCustomerByPhone', this.state.searchPhone, 'IGNORE_CUSTOMER_NOTES');
		if (result.ok) {
			const createdCustomer = await CustomerFacade.getForEdition(result.value.Id);
			// No muestro las notas del cliente porque ya las habrá mostrado el searchCustomerByPhoneCommand
			this.showCustomer(createdCustomer);
		}
	};

	private addCustomer = async () => {
		const result = await Command.execute('createCustomer', this.state.searchPhone);
		if (!result.ok)
			return;

		if (result.value.Telephone === '') {
			await Message.show(Res.Msg.MSG_CUSTOMER_MUST_HAVE_A_PHONE());
			return;
		}

		if (result.ok) {
			this.showCustomer(result.value);
		}
	};

	private clearState() {
		this.setState(TakeOut.emptyData(), async () => {
			await this.form.current?.remount('customer.FiscalName', 'customer.ContactPerson', 'customer.Telephone', 'customer.Email');
		});
	}

	private showCustomer(customer: CustomerForEditionDTO) {
		const newState = TakeOut.setCustomer(this.state, customer);
		this.setState(newState, async () => {
			await this.form.current?.remount('customer.FiscalName', 'customer.ContactPerson', 'customer.Telephone', 'customer.Email');
		});
	}

	private onAddDeliveryAddress = async () => {
		await this.form.current?.updateModel();

		const result = await createDeliveryAddress(this.form.current!.model.customer);
		if (result.ok) {
			this.showCustomer(await CustomerFacade.getForEdition(this.state.customer.Id));
			return;
		}

		this.txtPhone.current!.activate();
	};

	private onRemoveDeliveryAddress = async (address: AddressDTO) => {
		if (isBillingAddress(this.state.customer.Address, address)) {
			await Message.show(Res.Msg.MSG_CANNOT_DELETE_BILLING_ADDRESS());
			return;
		}

		const result = await Message.confirm(Res.Msg.MSG_CONFIRM_DELIVERY_ADDRESS_DELETION());
		if (result.ok) {
			await CustomerFacade.removeDeliveryAddress(this.state.customer.Id, address);
			this.showCustomer(await CustomerFacade.getForEdition(this.state.customer.Id));
		}
	};

	private buildOrderDataForm = () => {
		const { Form, ComboBox, TimeBox } = UI.controlsFor<State>();
		const { DatePicker } = legacyControlsFor<State>();

		return <Form ref={this.form} model={this.state}>

			<Flex.Horizontal>
				<Flex.Item size='2ex' className='m-l-md m-r-md text-muted text-26'>
					<i className='fa fa-money'></i>
				</Flex.Item>
				<Flex.Item width='*' className='m-r-md'>
					<div className='m-b text-18'>{Res.Web.START_TAKEOUT_ORDER_DATA_GRP()}</div>
					<ComboBox label={Res.Web.START_TAKEOUT_ORDER_TYPE_LBL()} linkTo={x => x.orderType} options={[SalesOrderType.Delivery, SalesOrderType.TakeAway]} format={SalesOrderType.toString} required />
					<DatePicker label={Res.Web.START_TAKEOUT_ORDER_DATE_LBL()} linkTo={x => x.deliveryDate} required />
					<TimeBox label={Res.Web.START_TAKEOUT_ORDER_TIME_LBL()} linkTo={x => x.deliveryTime} placeholder={'HH:mm'} required />
				</Flex.Item>
			</Flex.Horizontal>
		</Form>;
	};

	private buildCustomerDataInfo = () => {

		if (this.state.customer.Id === 0) {
			return <p className='p-lg text-center text-muted text-26 font-light text-italic'>{Res.Msg.MSG_PHONE_NUMBER_IS_REQUIRED_TO_SEARCH_CUSTOMER()}</p>;
		}

		return <Flex.Horizontal>
			<Flex.Item size='2ex' className='text-muted text-26 m-l-md'>
				<i className='fa fa-id-card-o' />
			</Flex.Item>
			<Flex.Item size='*' className='m-l-md'>
				<Flex.Vertical>
					<Flex.Item className='text-18 m-b-xl'>
						{this.state.customer.FiscalName ? this.state.customer.FiscalName : Res.Web.START_TAKEOUT_ORDER_CUSTOMER_DATA_GRP()}
						{[this.state.customer.Telephone, this.state.customer.Email, this.state.customer.ContactPerson].map((text, idx) => <React.Fragment key={idx}>
							<br /><small>{text}</small>
						</React.Fragment>)}
					</Flex.Item>
					<Flex.Item size='*'>
						<DeliveryAddresses currentAddress={this.state.currentAddress}
							deliveryAddresses={this.state.deliveryAddresses}
							onAddDeliveryAddress={this.onAddDeliveryAddress}
							onSelectDeliveryAddress={(address) => this.setState(TakeOut.selectAddress(this.state, address))}
							onRemoveDeliveryAddress={this.onRemoveDeliveryAddress} />
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
		</Flex.Horizontal>;
	};

	private buildPhoneForm() {

		const { Form, TextBox } = UI.controlsFor<State>();
		return <Form ref={this.form} model={this.state}>
			<TextBox ref={this.txtPhone} label={Res.Web.START_TAKEOUT_ORDER_PHONE_LBL()} linkTo={x => x.searchPhone} onValueChange={this.searchByPhone}>
				<UI.TextBoxButton iconClassName='fa fa-search text-info' onClick={this.searchCustomer} />
				<UI.TextBoxButton iconClassName='fa fa-plus text-info' onClick={this.addCustomer} />
			</TextBox>
		</Form>;
	}

	protected getContent(): React.ReactNode {

		return <Flex.Vertical>
			<Flex.Item >
				<div style={{ width: 400 }} className='m-t m-l-xl text-16'>{this.buildPhoneForm()}</div>
			</Flex.Item>
			<Flex.Item size='*'>
				<Flex.Horizontal>
					<Flex.Item size='*' className='m-t-md'>
						{this.buildCustomerDataInfo()}
					</Flex.Item>
					<Flex.Item size='*' className='m-t-md'>
						<Flex.Vertical>
							<Flex.Item size='*' style={{ alignSelf: 'center' }}>
								{this.buildOrderDataForm()}
							</Flex.Item>
							<Flex.Item style={{ alignSelf: 'end' }}>
								<div style={{ width: 400 }}>
									<Keypad isFullScreen={false} isLargeDevice={ThemeManager.isLargeDevice()} alwaysVisible />
								</div>
							</Flex.Item>
						</Flex.Vertical>
					</Flex.Item>
				</Flex.Horizontal>
			</Flex.Item>
		</Flex.Vertical>;
	}
}

declare global {
	interface CommandType {
		startTakeOutOrder: () => Promise<Result>
	}
}

Command.register('startTakeOutOrder', Preconditions.and(Preconditions.and(Preconditions.ensure(Permission.SalesOrder_ShowTakeOutOrders), Preconditions.ensure(Permission.Ticket_SetBuyerCustomer)), Preconditions.ensureIsNotLite), async (): Promise<Result> => {
	const result = await Modal.show(StartTakeOutOrdersScreen);
	if (result.ok) {
		await SalesStore.release();
		await SalesStore.startTakeOutOrder(result.value);
	}

	return result.ok ? Result.ok() : Result.error();
});